// src/modules/auth/pages/RegisterPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { RegisterForm } from '../components/RegisterForm/RegisterForm';
import { businessService } from '../../../services/business.service';
import type { RegisterFormValues } from '../types';
import type { CreateBusinessRequest } from '../../../services/business.service';
import background from '../../../assets/auth/login.png';
import logo from '../../../assets/logo/text-logo.png';

export const RegisterPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: RegisterFormValues) => {
    try {
      setIsLoading(true);
      const businessData: CreateBusinessRequest = {
        business_name: values.business_name,
        owner: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          phone: values.phoneNumber
        }
      };

      const businessResponse = await businessService.createBusiness(businessData);
      
      if (!businessResponse.success) {
        throw new Error(businessResponse.message || 'Failed to create business');
      }

      toast.success('Business created successfully!');
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      {/* Right side - Logo */}
      {!isMobile && (
        <Box
          sx={{
            width: '50%',
            height: '100%',
            bgcolor: '#008B9B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            component="img"
            src={background}
            alt="VenFlows"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </Box>
      )}

      {/* Left side - Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 4, sm: 6 },
          bgcolor: '#fff',
          overflow: 'auto'
        }}
      >
        <Box sx={{ maxWidth: '440px', mx: 'auto', width: '100%' }}>
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                width: '80%',
                mb: 4
              }}
            >
              <img
                src={logo}
                alt="VenFlows"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>
            <Typography 
              variant="h4"
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', md: '2rem' },
                mb: 1
              }}
            >
              Sign up to your account
            </Typography>
            <Typography 
              variant="body2"
              sx={{ 
                color: 'text.secondary'
              }}
            >
              Welcome! Please enter your details.
            </Typography>
          </Box>

          <RegisterForm
            onSubmit={handleSubmit}
            isLoading={isLoading}
          />

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              Already have an account?{' '}
              <RouterLink 
                to="/auth/login"
                style={{
                  color: '#00BCD4',
                  textDecoration: 'none',
                  fontWeight: 500
                }}
              >
                Sign in
              </RouterLink>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
